import { useMemo, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { RiLogoutBoxRLine } from '@remixicon/react';
import {
  Button,
  CheckLine,
  Colors,
  DropdownButton,
  Input,
  PlusLine,
  PrimaryColors,
  Flex,
  Text,
} from '@revenuemarket/cds';
import { colorTokens, typographyStyle } from '@revenuemarket/design';
import { CUSTOM_ATTRIBUTES } from '@revenuemarket/utils/customAttribute';
import { MenuProps, Tooltip, Typography } from 'antd';
import { useRouter } from 'next/router';
import { ItemType } from 'rc-menu/lib/interface';
import { CompanyUserPermission } from '~apis/role/type';
import { CompanyUserResponse } from '~apis/user';
import { permissionGuardFilter } from '~components/Role/PermissionGuard';
import { DEMO_TRIAL_TOOLTIP_MSG } from '~constants/clobe';
import { useSignOut } from '~hooks/queries/auth/query';
import { useCompany, useCompanyMember } from '~hooks/queries/companies/query';
import { useUserPermissions } from '~hooks/queries/role/query';
import { useGetTaxAgent } from '~hooks/queries/setting/query';
import { useUserCompanies } from '~hooks/queries/users/query';
import { useCompanyId } from '~hooks/useCompanyId';
import useIsDemoTrial from '~hooks/useIsDemoTrial';
import useSuperUserHook from '~hooks/useSuperUserHook';
import { useUserId } from '~hooks/useUserId';
import { css } from '~libs/emotion';
import { mixpanel } from '~pages/_app';

const CompanyInfoDropDown = () => {
  const userId = useUserId();
  const { companyId, setCompanyId } = useCompanyId();
  const router = useRouter();
  const session = useSuperUserHook();
  const { data: companyUserDetail } = useCompanyMember({ companyId, memberId: userId });
  const { data: userPermissionsData } = useUserPermissions();
  const companyUserDetailData = companyUserDetail?.Data?.data;
  const userPermissions = userPermissionsData?.Data?.data?.companyUserPermissions ?? [];
  const { data: userCompaniesData } = useUserCompanies({ userId });
  const [companyNameFilter, setCompanyNameFilter] = useState<string>();
  const isDemoTrial = useIsDemoTrial();
  const { data: taxAgent } = useGetTaxAgent();

  const taxAgentExists = taxAgent?.Data?.data?.taxAgentId != null;

  const userCompanies = useMemo(
    () => userCompaniesData?.Data?.data?.items.filter((company) => company.role !== 'BANNED') || [],
    [userCompaniesData],
  );

  const { data: selectedCompanyData, isLoading: isSelectedCompanyLoading } = useCompany({
    companyId,
  });
  const selectedCompany = selectedCompanyData?.Data?.data;

  const { mutateAsync: signOut } = useSignOut();

  const menuCompanyInfo = {
    label: (
      <Typography.Text
        style={{
          fontSize: 14,
        }}
        {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.COMPANY_INFO_MENU_ITEM}
      >
        회사 정보
      </Typography.Text>
    ),
    key: '2',
    onClick: () => {
      router.push('/settings/company');
    },
  };
  const menuMemberInfo = {
    label: (
      <Typography.Text
        style={{
          fontSize: 14,
        }}
        {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.MEMBER_INFO_MENU_ITEM}
      >
        구성원 정보
      </Typography.Text>
    ),
    key: '3',
    onClick: () => {
      router.push('/settings/member');
    },
  };
  const menuTaxAgentInfo = {
    label: (
      <Typography.Text
        style={{
          fontSize: 14,
        }}
        {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.TAX_AGENT_INFO_MENU_ITEM}
      >
        세무대리인
      </Typography.Text>
    ),
    key: 'tax-agent',
    onClick: () => {
      router.push('/settings/tax-agent');
    },
  };
  const menuUserInfo = {
    label: (
      <Flex direction="column">
        <Typography.Text
          style={{
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {companyUserDetailData?.name || session?.data?.userName}님
        </Typography.Text>
        <Typography.Text
          style={{
            fontSize: 12,
            color: colorTokens.textDescription,
          }}
        >
          {companyUserDetailData?.email}
        </Typography.Text>
      </Flex>
    ),
    disabled: true,
    key: '0',
  };
  const menuUserSettings = {
    label: (
      <Typography.Text
        style={{
          fontSize: 14,
        }}
        {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.USER_SETTINGS_MENU_ITEM}
      >
        계정 설정
      </Typography.Text>
    ),
    key: '1',
    onClick: () => {
      router.push('/settings/me');
    },
  };
  const menuLogout = {
    label: (
      <Flex
        align="center"
        gap={4}
        onClick={() => {
          signOut();
          mixpanel.reset();
        }}
        {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.LOGOUT_MENU_ITEM}
      >
        <RiLogoutBoxRLine size={16} color={colorTokens.iconDefault} />
        <Typography.Text
          style={{
            fontSize: 14,
          }}
        >
          로그아웃
        </Typography.Text>
      </Flex>
    ),
    key: '4',
    className: css`
      margin-bottom: 8px !important;
    `,
  };
  const menuRoleInfo = {
    label: (
      <Typography.Text
        style={{
          fontSize: 14,
        }}
      >
        역할 관리
      </Typography.Text>
    ),
    key: '5',
    onClick: () => {
      router.push('/settings/role');
    },
  };

  const menuCreateWorkspace = {
    label: (
      <Flex justify="center" style={{ margin: '0px -8px' }}>
        <Button
          style={{ width: '100%' }}
          buttonType="primary"
          buttonSize="small"
          icon={<PlusLine fill={PrimaryColors.primary700} />}
          border
          onClick={() => {
            router.push('/auth/create-workspace');
          }}
          {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.CREATE_WORKSPACE_BUTTON}
        >
          워크스페이스 생성
        </Button>
      </Flex>
    ),
    key: 'createWorkspace',
    className: css`
      &:hover {
        background-color: transparent !important;
        cursor: default !important;
        color: inherit !important;
      }
    `,
  };

  const roleGuardMenuCompanyInfo = permissionGuardFilter<ItemType>({
    required: CompanyUserPermission.CAN_MANAGE_COMPANY,
    userPermissions,
    target: menuCompanyInfo,
  });

  const roleGuardMenuMemberInfo = permissionGuardFilter<ItemType>({
    required: CompanyUserPermission.CAN_MANAGE_COMPANY_USERS,
    userPermissions,
    target: menuMemberInfo,
  });

  const roleGuardMenuRoleInfo = permissionGuardFilter<ItemType>({
    required: CompanyUserPermission.CAN_MANAGE_COMPANY_USERS,
    userPermissions,
    target: menuRoleInfo,
  });

  const items: MenuProps['items'] = [
    {
      label: (
        <Flex direction="column" gap={4} style={{ marginTop: '8px' }}>
          <Typography.Text style={{ ...typographyStyle['16_600'], color: Colors.gray700 }}>
            {selectedCompany?.name}
          </Typography.Text>
        </Flex>
      ),
      key: 'selectedCompany',
      disabled: true,
    },
    roleGuardMenuCompanyInfo,
    roleGuardMenuMemberInfo,
    taxAgentExists ? menuTaxAgentInfo : null,
    roleGuardMenuRoleInfo,
    {
      type: 'divider',
    },
    {
      label: selectedCompany && (
        <Flex direction="column" gap={4}>
          <Input
            placeholder="회사명 검색"
            value={companyNameFilter}
            onChange={(e) => {
              setCompanyNameFilter(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />

          <Flex
            direction="column"
            style={{
              maxHeight: '340px',
              overflowY: 'scroll',
              marginRight: '-8px',
            }}
            className={css`
              &::-webkit-scrollbar {
                width: 8px;
              }
            `}
          >
            {userCompanies
              ?.filter((company) => {
                if (!companyNameFilter) return true;
                return company.companyName.includes(companyNameFilter);
              })
              ?.map((company: CompanyUserResponse) => {
                const isSelected = selectedCompany.id === company.companyId;
                const typoStyle = isSelected
                  ? typographyStyle['14_500']
                  : typographyStyle['14_400'];
                return (
                  <Flex
                    key={company.companyId}
                    justify="space-between"
                    gap={4}
                    style={{
                      padding: '4px 8px',
                      cursor: 'pointer',
                      borderRadius: '8px',
                    }}
                    className={css`
                      &:hover {
                        background-color: ${Colors.grayOpacity04};
                      }
                    `}
                    onClick={async () => {
                      await setCompanyId(company.companyId);
                      router.reload();
                    }}
                  >
                    <Typography.Text
                      key={company.companyId}
                      style={{
                        ...typoStyle,
                        color: Colors.gray700,
                      }}
                      {...CUSTOM_ATTRIBUTES.COMPANY_DROPDOWN_MENU.COMPANY_SELECTOR(
                        company.companyId,
                        company.companyName,
                        isSelected,
                      )}
                    >
                      {company.companyName}
                    </Typography.Text>
                    {isSelected && <CheckLine width={18} height={18} />}
                  </Flex>
                );
              })}
          </Flex>
        </Flex>
      ),
      key: 'companies',
      className: css`
        border-radius: 8px !important;
        background-color: ${Colors.grayOpacity03} !important;

        &:hover {
          cursor: default !important;
          color: inherit !important;
        }
      `,
    },
    menuCreateWorkspace,
    {
      type: 'divider',
    },
    menuUserInfo,
    menuUserSettings,
    menuLogout,
  ];

  const displayTitle = useMemo(() => {
    if (isSelectedCompanyLoading) {
      return (
        <Flex align="center" gap={6}>
          <LoadingOutlined spin style={{ color: Colors.gray600, fontSize: '12px' }} />
        </Flex>
      );
    }

    return <Text textStyle="14_400">{selectedCompany?.name ?? '워크스페이스'}</Text>;
  }, [selectedCompany, isSelectedCompanyLoading]);

  return (
    <Tooltip placement="top" title={isDemoTrial ? DEMO_TRIAL_TOOLTIP_MSG : undefined}>
      <div>
        <div
          style={{
            pointerEvents: isDemoTrial ? 'none' : 'auto',
          }}
        >
          <DropdownButton
            disabled={isDemoTrial}
            disableScroll
            title={displayTitle}
            menuProps={{
              className: css`
                &&& .ant-dropdown-menu-item {
                  padding: 6px 8px !important;
                  margin-left: 4px;
                  margin-right: 4px;
                }

                &&& .ant-dropdown-menu-item-selected {
                  background-color: white;
                }

                &&& .ant-dropdown-menu-title-content {
                  font-size: 14px;
                  ${typographyStyle['14_400']}
                }
              `,
            }}
            overlayStyle={{
              width: 275,
            }}
            buttonProps={{
              style: {
                width: 175,
                pointerEvents: isDemoTrial ? 'none' : 'auto',
              },
              ...CUSTOM_ATTRIBUTES.COMPANY_INFO_DROPDOWN,
            }}
            menu={{
              items,
            }}
          ></DropdownButton>
        </div>
      </div>
    </Tooltip>
  );
};

export default CompanyInfoDropDown;
